import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X, Search, Filter, Play, AlertCircle, CheckCircle, XCircle, Clock, User, RefreshCw, Loader2, Maximize2, ChevronLeft, ChevronRight, ChevronRight as ArrowRight, Database, GitBranch, ZoomIn, ZoomOut } from 'lucide-react';
import NavigatedViewer from 'bpmn-js/lib/NavigatedViewer';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import StartProcessDialog from '../../components/processes/StartProcessDialog';
// Note: the studio version of "Start Process" lives in
// components/processes/StartProcessDialog.tsx — it loads the start form,
// renders it via formiojs v4, and starts the instance. Both this page and
// StudioPage use the same dialog so they stay visually identical.
import Sidebar from '../components/ui/Sidebar';
import { ListItemSkeleton, CardSkeleton } from '../components/ui/Skeleton';
import { toast } from 'sonner';
import { getCamundaService } from '../../infrastructure/camunda';
import type { ProcessDefinitionStatistics, HistoricProcessInstance } from '../../infrastructure/camunda';

interface ProcessItem {
  id: string;
  name: string;
  version: string;
  definitionKey: string;
  definitionId: string;
  deploymentId: string;
  lastDeployed: string;
  status: 'running' | 'canceled' | 'incidents' | 'completed';
  tenant: string | null;
  incidents: number;
  runningInstances: number;
  failedJobs: number;
}

interface ProcessHistoryItem {
  id: string;
  user: string;
  userAvatar?: string;
  time: string;
  instanceId: string;
  status: 'running' | 'incidents' | 'completed' | 'canceled';
  description: string;
  businessKey: string | null;
  duration?: string;
  currentActivity?: string;
  progress?: number;
  variables?: number;
  activeTasks?: number;
  hasIncident?: boolean;
}

const ProcessesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const definitionParam = searchParams.get('definition');
  const actionParam = searchParams.get('action');
  const [selectedProcess, setSelectedProcess] = useState<ProcessItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('7 days');
  const [processes, setProcesses] = useState<ProcessItem[]>([]);
  const [processHistory, setProcessHistory] = useState<ProcessHistoryItem[]>([]);
  const [runningInstances, setRunningInstances] = useState<ProcessHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showFullDialog, setShowFullDialog] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'running' | 'history' | 'bpmn'>('running');
  const [drawerPage, setDrawerPage] = useState(0);

  // BPMN State
  const [bpmnXml, setBpmnXml] = useState<string | null>(null);
  const [bpmnLoading, setBpmnLoading] = useState(false);
  const [activityStats, setActivityStats] = useState<any[]>([]);
  const [zoom, setZoom] = useState(100);
  const bpmnContainerRef = React.useRef<HTMLDivElement>(null);
  const bpmnViewerRef = React.useRef<any>(null);

  // Start-Process dialog plumbing. The dialog component is self-contained
  // (loads its own form schema, manages its own businessKey state, calls
  // startById on submit) so all we need to track here is whether it's open
  // and which process row triggered it.
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [processToStart, setProcessToStart] = useState<ProcessItem | null>(null);

  const pageSize = 10;
  const drawerPageSize = 5;

  const camunda = getCamundaService();

  // Fetch process definitions with statistics
  const fetchProcesses = useCallback(async () => {
    try {
      setLoading(true);
      const [stats, sortedDefinitions, deployments] = await Promise.all([
        camunda.processDefinition.getStatistics({
          failedJobs: true,
          incidents: true,
        }),
        camunda.processDefinition.getList({
          sortBy: 'deployTime',
          sortOrder: 'desc',
        }),
        camunda.deployment.getList(),
      ]);

      const deploymentMap = new Map(deployments.map(d => [d.id, d.deploymentTime]));

      const processItems: ProcessItem[] = stats.map((stat: ProcessDefinitionStatistics) => {
        const def = stat.definition || stat;
        const totalIncidents = stat.incidents?.reduce((sum, inc) => sum + inc.incidentCount, 0) || 0;

        let status: ProcessItem['status'] = 'completed';
        if (stat.instances > 0) status = 'running';
        if (totalIncidents > 0) status = 'incidents';
        if (stat.failedJobs > 0) status = 'incidents';

        return {
          id: def.id,
          name: def.name || def.key,
          version: `v${def.version}`,
          definitionKey: def.key,
          definitionId: def.id,
          deploymentId: def.deploymentId,
          lastDeployed: deploymentMap.get(def.deploymentId) ? new Date(deploymentMap.get(def.deploymentId)!).toLocaleString() : 'Unknown',
          status,
          tenant: def.tenantId,
          incidents: totalIncidents,
          runningInstances: stat.instances,
          failedJobs: stat.failedJobs,
        };
      });

      // Sort items based on the sorted definitions order
      // We use a Map for O(1) lookup time to avoid O(N^2) complexity in sorting
      const definitionIndexMap = new Map(sortedDefinitions.map((d, i) => [d.id, i]));

      const sortedItems = processItems.sort((a, b) => {
        const indexA = definitionIndexMap.has(a.id) ? definitionIndexMap.get(a.id)! : Number.MAX_SAFE_INTEGER;
        const indexB = definitionIndexMap.has(b.id) ? definitionIndexMap.get(b.id)! : Number.MAX_SAFE_INTEGER;
        return indexA - indexB;
      });

      setProcesses(sortedItems);

      // Auto-select process if definition param is present
      if (definitionParam) {
        const matchedProcess = processItems.find(p => p.definitionKey === definitionParam);
        if (matchedProcess) {
          if (actionParam === 'start') {
            // Need to wrap in timeout to avoid firing before render finishes if openStartDialog sets state
            setTimeout(() => {
              openStartDialog(matchedProcess, { stopPropagation: () => {} } as React.MouseEvent);
            }, 0);
          } else {
            setSelectedProcess(matchedProcess);
            setShowFullDialog(true);
          }
          // Clear the URL param
          setSearchParams({});
        }
      }
    } catch (error) {
      console.error('Error fetching processes:', error);
      toast.error('Failed to fetch process definitions');
    } finally {
      setLoading(false);
    }
  }, [camunda]);

  // Fetch process history for selected process
  const fetchProcessHistory = useCallback(async (processDefinitionId: string) => {
    try {
      setHistoryLoading(true);
      const history = await camunda.history.getProcessInstances({
        processDefinitionId,
        sortBy: 'startTime',
        sortOrder: 'desc',
        maxResults: 50,
      });

      const mapInstance = (instance: HistoricProcessInstance): ProcessHistoryItem => {
        let status: ProcessHistoryItem['status'] = 'running';
        const hasIncident = false; // Would come from incident API
        if (instance.state === 'COMPLETED') status = 'completed';
        else if (instance.state === 'EXTERNALLY_TERMINATED' || instance.state === 'INTERNALLY_TERMINATED') status = 'canceled';
        else if (instance.state === 'SUSPENDED') status = 'incidents';

        // Calculate duration
        const startTime = instance.startTime ? new Date(instance.startTime) : null;
        const endTime = instance.endTime ? new Date(instance.endTime) : new Date();
        let duration = 'N/A';
        if (startTime) {
          const diffMs = endTime.getTime() - startTime.getTime();
          const hours = Math.floor(diffMs / (1000 * 60 * 60));
          const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
          duration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        }

        // Mock progress for running instances (would come from actual activity data)
        const progress = status === 'running' ? Math.floor(Math.random() * 60) + 20 : 100;

        return {
          id: instance.id,
          user: instance.startUserId || 'system',
          time: instance.startTime ? new Date(instance.startTime).toLocaleString() : 'Unknown',
          instanceId: instance.id,
          status,
          description: instance.deleteReason || `Process ${status}`,
          businessKey: instance.businessKey,
          duration,
          currentActivity: status === 'running' ? 'Processing...' : undefined,
          progress,
          variables: Math.floor(Math.random() * 10) + 1,
          activeTasks: status === 'running' ? Math.floor(Math.random() * 3) + 1 : 0,
          hasIncident,
        };
      };

      const allItems = history.map(mapInstance);

      // Separate running and completed/history instances
      const running = allItems.filter(item => item.status === 'running');
      const completed = allItems.filter(item => item.status !== 'running');

      setRunningInstances(running);
      setProcessHistory(completed);
      setDrawerPage(0);
    } catch (error) {
      console.error('Error fetching process history:', error);
      toast.error('Failed to fetch process history');
    } finally {
      setHistoryLoading(false);
    }
  }, [camunda]);

  // Open the start-process dialog for a given row. The dialog itself
  // fetches the start form, owns the businessKey input, and calls
  // startById — we just point it at the right process definition and
  // refresh the list after a successful start (via `onStarted`).
  const openStartDialog = (process: ProcessItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessToStart(process);
    setShowStartDialog(true);
  };

  // Initial load
  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  // Load history when process is selected
  useEffect(() => {
    if (selectedProcess) {
      fetchProcessHistory(selectedProcess.definitionId);
    }
  }, [selectedProcess, fetchProcessHistory]);

  // Fetch BPMN Data when tab changes
  useEffect(() => {
    if (showFullDialog && selectedProcess && drawerTab === 'bpmn') {
      const fetchData = async () => {
        setBpmnLoading(true);
        try {
          const xmlData = await camunda.processDefinition.getXmlById(selectedProcess.definitionId);
          setBpmnXml(xmlData.bpmn20Xml);

          const stats = await camunda.processDefinition.getActivityStatisticsById(selectedProcess.definitionId, { incidents: true });
          setActivityStats(stats);
        } catch (error) {
          console.error('Error fetching BPMN data:', error);
          toast.error('Failed to load process diagram');
        } finally {
          setBpmnLoading(false);
        }
      };

      fetchData();
    }
  }, [showFullDialog, selectedProcess, drawerTab]);

  // Render BPMN
  useEffect(() => {
    if (!bpmnContainerRef.current || !bpmnXml || drawerTab !== 'bpmn') return;

    // Cleanup previous viewer
    if (bpmnViewerRef.current) {
      bpmnViewerRef.current.destroy();
    }

    const viewer = new NavigatedViewer({
      container: bpmnContainerRef.current,
      height: '100%',
      width: '100%'
    });

    bpmnViewerRef.current = viewer;

    viewer.importXML(bpmnXml).then(() => {
      const canvas = viewer.get('canvas') as any;
      canvas.zoom('fit-viewport');
      setZoom(Math.round(canvas.zoom() * 100));

      // Overlays
      const overlays = viewer.get('overlays') as any;
      const elementRegistry = viewer.get('elementRegistry') as any;

      activityStats.forEach((stat: any) => {
        try {
          const element = elementRegistry.get(stat.id);
          if (element) {
            if (stat.instances > 0) {
              overlays.add(stat.id, {
                position: { bottom: 10, right: 10 },
                html: `<div style="background-color: #3b82f6; color: white; border-radius: 9999px; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">${stat.instances}</div>`
              });
            }
            const incidentCount = stat.incidents ? stat.incidents.reduce((acc: number, inc: any) => acc + inc.incidentCount, 0) : 0;
            if (incidentCount > 0) {
              overlays.add(stat.id, {
                position: { bottom: 10, right: -15 },
                html: `<div style="background-color: #ef4444; color: white; border-radius: 9999px; width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: bold; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">${incidentCount}</div>`
              });
            }
          }
        } catch (e) {
          // ignore elements not found in diagram
        }
      });
    }).catch((err: any) => console.error('BPMN import error', err));

    return () => {
      if (bpmnViewerRef.current) {
        bpmnViewerRef.current.destroy();
      }
    };
  }, [bpmnXml, activityStats, drawerTab, showFullDialog]);

  // Filter processes by search query
  const filteredProcesses = processes.filter(process =>
    process.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    process.definitionKey.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusIcon = (status: ProcessItem['status']) => {
    switch (status) {
      case 'running':
        return <Clock size={16} className="text-blue-600" />;
      case 'canceled':
        return <XCircle size={16} className="text-gray-600" />;
      case 'incidents':
        return <AlertCircle size={16} className="text-red-600" />;
      case 'completed':
        return <CheckCircle size={16} className="text-green-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: ProcessItem['status']) => {
    switch (status) {
      case 'running':
        return 'bg-blue-100 text-blue-700';
      case 'canceled':
        return 'bg-gray-100 text-gray-700';
      case 'incidents':
        return 'bg-red-100 text-red-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };


  // Pagination
  const totalPages = Math.ceil(filteredProcesses.length / pageSize);
  const paginatedProcesses = filteredProcesses.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Play size={20} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Processes</h1>
                  <p className="text-sm text-gray-500">View and run your processes</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search processes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-64 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                  />
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {['24h', '7d', '30d'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setSelectedTimeFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${selectedTimeFilter === filter
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <button className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  <Filter size={14} className="text-gray-500" />
                  <span className="text-gray-600">Filter</span>
                </button>
                <button
                  onClick={() => fetchProcesses()}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Main Content */}
            <div className="flex-1 p-6 overflow-auto">
              {/* Stats Bar */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">{filteredProcesses.length} processes found</span>
              </div>

              {/* Process List */}
              <div className="bg-white rounded-lg border border-gray-200">
                <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 text-sm font-medium text-gray-700">
                  <div className="col-span-4">Process</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-2">Tenant</div>
                  <div className="col-span-2">Last Deployed</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>

                {loading ? (
                  <>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <ListItemSkeleton key={i} />
                    ))}
                  </>
                ) : paginatedProcesses.length === 0 ? (
                  <div className="flex items-center justify-center p-8 text-gray-500">
                    No processes found
                  </div>
                ) : (
                  paginatedProcesses.map((process) => (
                    <div
                      key={process.id}
                      className={`grid grid-cols-12 gap-4 p-4 border-b border-gray-200 transition-all duration-200 cursor-pointer ${selectedProcess?.id === process.id
                        ? 'bg-blue-50 border-l-4 border-l-blue-600 shadow-inner'
                        : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                        }`}
                      onClick={() => setSelectedProcess(process)}
                    >
                      <div className="col-span-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(process.status)}
                          <div>
                            <div className="font-medium text-gray-900">
                              {process.name} {process.version}
                            </div>
                            <div className="text-sm text-gray-500">{process.definitionKey}</div>
                            <div className="text-xs text-gray-400">{process.definitionId}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(process.status)}`}>
                            {process.runningInstances} running
                          </span>
                          {process.incidents > 0 && (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-medium">
                              {process.incidents} incidents
                            </span>
                          )}
                          {process.failedJobs > 0 && (
                            <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-medium">
                              {process.failedJobs} failed
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-gray-900">{process.tenant || 'Default'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-sm text-gray-600">{process.lastDeployed}</span>
                      </div>
                      <div className="col-span-2 text-right">
                        <button
                          onClick={(e) => openStartDialog(process, e)}
                          className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          <Play size={14} />
                          Run
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between p-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, filteredProcesses.length)} of {filteredProcesses.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 text-sm rounded-lg ${currentPage === i
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Process Details with Transition */}
            <div
              className={`bg-white border-l border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${selectedProcess ? 'w-96 opacity-100' : 'w-0 opacity-0 overflow-hidden'
                }`}
            >
              {selectedProcess && (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getStatusIcon(selectedProcess.status)}
                        <h2 className="text-base font-semibold text-gray-900 truncate">
                          {selectedProcess.name} {selectedProcess.version}
                        </h2>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowFullDialog(true)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Open full details"
                        >
                          <Maximize2 size={16} />
                        </button>
                        <button
                          onClick={() => setSelectedProcess(null)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedProcess.status)}`}>
                      {selectedProcess.status.charAt(0).toUpperCase() + selectedProcess.status.slice(1)}
                    </span>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto p-4 scrollbar-visible" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                    {/* Process Statistics */}
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Statistics</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col justify-center items-center h-full text-center p-2 bg-white rounded border border-gray-100">
                          <div className="text-lg font-bold text-blue-600">{selectedProcess.runningInstances}</div>
                          <div className="text-xs text-gray-500">Running</div>
                        </div>
                        <div className="flex flex-col justify-center items-center h-full text-center p-2 bg-white rounded border border-gray-100">
                          <div className="text-lg font-bold text-red-600">{selectedProcess.incidents}</div>
                          <div className="text-xs text-gray-500">Incidents</div>
                        </div>
                        <div className="flex flex-col justify-center items-center h-full text-center p-2 bg-white rounded border border-gray-100">
                          <div className="text-lg font-bold text-yellow-600">{selectedProcess.failedJobs}</div>
                          <div className="text-xs text-gray-500">Failed Jobs</div>
                        </div>
                        <div className="flex flex-col justify-center items-center h-full text-center p-2 bg-white rounded border border-gray-100">
                          <div className="text-xs font-medium text-gray-700">{selectedProcess.lastDeployed}</div>
                          <div className="text-xs text-gray-500">Last Deployed</div>
                        </div>
                      </div>
                    </div>

                    {/* Process Definition Key */}
                    <div className="mb-4">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Definition Key</h3>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded block break-all">{selectedProcess.definitionKey}</code>
                    </div>

                    {/* Tabs for Running/History */}
                    <div className="border-b border-gray-200 mb-3">
                      <div className="flex">
                        <button
                          onClick={() => { setDrawerTab('running'); setDrawerPage(0); }}
                          className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${drawerTab === 'running'
                            ? 'text-blue-600 border-blue-600'
                            : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                        >
                          Running ({runningInstances.length})
                        </button>
                        <button
                          onClick={() => { setDrawerTab('history'); setDrawerPage(0); }}
                          className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${drawerTab === 'history'
                            ? 'text-blue-600 border-blue-600'
                            : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                        >
                          History ({processHistory.length})
                        </button>
                        <button
                          onClick={() => { setDrawerTab('bpmn'); setDrawerPage(0); }}
                          className={`flex-1 py-2 text-xs font-medium border-b-2 transition-colors ${drawerTab === 'bpmn'
                            ? 'text-blue-600 border-blue-600'
                            : 'text-gray-500 border-transparent hover:text-gray-700'
                            }`}
                        >
                          Flow Diagram
                        </button>
                      </div>
                    </div>

                    {/* Instance List or Diagram */}
                    {drawerTab === 'bpmn' ? (
                      <div className="h-[500px] flex flex-col relative bg-gray-50 rounded-xl overflow-hidden border border-gray-200 [&_.bjs-powered-by]:hidden">
                        {/* Diagram Controls */}
                        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                          <div className="bg-white p-2 rounded-lg shadow border border-gray-100 flex flex-col gap-2">
                            <button onClick={() => {
                              const canvas = bpmnViewerRef.current?.get('canvas');
                              if (canvas) { canvas.zoom(canvas.zoom() * 1.2); setZoom(Math.round(canvas.zoom() * 100)); }
                            }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Zoom In">
                              <ZoomIn size={18} />
                            </button>
                            <button onClick={() => {
                              const canvas = bpmnViewerRef.current?.get('canvas');
                              if (canvas) { canvas.zoom(canvas.zoom() / 1.2); setZoom(Math.round(canvas.zoom() * 100)); }
                            }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Zoom Out">
                              <ZoomOut size={18} />
                            </button>
                            <button onClick={() => {
                              const canvas = bpmnViewerRef.current?.get('canvas');
                              if (canvas) { canvas.zoom('fit-viewport'); setZoom(Math.round(canvas.zoom() * 100)); }
                            }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Fit to Screen">
                              <Maximize2 size={18} />
                            </button>
                          </div>
                        </div>

                        {bpmnLoading && (
                          <div className="absolute inset-0 z-20 bg-white/80 flex items-center justify-center">
                            <Loader2 size={32} className="animate-spin text-blue-600" />
                          </div>
                        )}

                        <div ref={!showFullDialog ? bpmnContainerRef : undefined} className="w-full h-full" />

                        <div className="absolute bottom-4 left-4 z-10 bg-white px-3 py-1.5 rounded-lg shadow border border-gray-100 text-xs font-medium text-gray-500">
                          Zoom: {zoom}%
                        </div>
                      </div>
                    ) : historyLoading ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <CardSkeleton key={i} />
                        ))}
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const items = drawerTab === 'running' ? runningInstances : processHistory;
                          const totalDrawerPages = Math.ceil(items.length / drawerPageSize);
                          const paginatedItems = items.slice(drawerPage * drawerPageSize, (drawerPage + 1) * drawerPageSize);

                          if (items.length === 0) {
                            return <p className="text-sm text-gray-500 text-center py-4">No {drawerTab === 'running' ? 'running instances' : 'history'} available</p>;
                          }

                          return (
                            <>
                              <div className="space-y-3">
                                {paginatedItems.map((item) => (
                                  <div
                                    key={item.id}
                                    className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md cursor-pointer transition-all duration-200 group"
                                    onClick={() => navigate(`/processes/instance/${item.instanceId}`)}
                                  >
                                    {/* Header Row */}
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="font-medium text-gray-900 text-sm truncate">
                                          {item.businessKey || selectedProcess?.name || 'Process Instance'}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${item.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                          item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                            item.status === 'canceled' ? 'bg-gray-100 text-gray-700' :
                                              'bg-red-100 text-red-700'
                                          }`}>
                                          {item.status === 'running' && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>}
                                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                        </span>
                                        {item.hasIncident && (
                                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                                            <AlertCircle size={10} />
                                            Incident
                                          </span>
                                        )}
                                      </div>
                                      <ArrowRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                                    </div>

                                    {/* Instance Info Row */}
                                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                      <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">{item.instanceId.substring(0, 12)}...</code>
                                      <span>•</span>
                                      <span>Started: {item.time}</span>
                                      <span>•</span>
                                      <span>Duration: {item.duration}</span>
                                    </div>

                                    {/* Progress Section (only for running) */}
                                    {item.status === 'running' && item.currentActivity && (
                                      <div className="mb-3">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-xs text-blue-600">Current Activity: {item.currentActivity}</span>
                                          <span className="text-xs font-medium text-gray-700">{item.progress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                                          <div
                                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                            style={{ width: `${item.progress}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Footer Stats */}
                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                      <div className="flex items-center gap-1">
                                        <Database size={12} className="text-gray-400" />
                                        <span>{item.variables} variables</span>
                                      </div>
                                      {item.status === 'running' && item.activeTasks && item.activeTasks > 0 && (
                                        <div className="flex items-center gap-1">
                                          <GitBranch size={12} className="text-gray-400" />
                                          <span>{item.activeTasks} active tasks</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Drawer Pagination */}
                              {totalDrawerPages > 1 && (
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                  <span className="text-xs text-gray-500">
                                    {drawerPage + 1} / {totalDrawerPages}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setDrawerPage(Math.max(0, drawerPage - 1))}
                                      disabled={drawerPage === 0}
                                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <ChevronLeft size={14} />
                                    </button>
                                    <button
                                      onClick={() => setDrawerPage(Math.min(totalDrawerPages - 1, drawerPage + 1))}
                                      disabled={drawerPage >= totalDrawerPages - 1}
                                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <ChevronRight size={14} />
                                    </button>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Full Screen Dialog */}
            {showFullDialog && selectedProcess && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowFullDialog(false)}>
                <div
                  className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Dialog Header */}
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Play size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {selectedProcess.name} {selectedProcess.version}
                        </h2>
                        <p className="text-sm text-gray-500">{selectedProcess.definitionKey}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProcess.status)}`}>
                        {selectedProcess.status.charAt(0).toUpperCase() + selectedProcess.status.slice(1)}
                      </span>
                    </div>
                    <button
                      onClick={() => setShowFullDialog(false)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  {/* Dialog Content */}
                  <div className="flex-1 overflow-y-auto p-6" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                    <div className="grid grid-cols-3 gap-6">
                      {/* Left Column - Details */}
                      <div className="col-span-1 space-y-6">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Process Details</h3>
                          <div className="space-y-3">
                            <div>
                              <label className="text-xs text-gray-500">Definition ID</label>
                              <code className="block text-sm bg-gray-100 px-2 py-1 rounded mt-1 break-all">{selectedProcess.definitionId}</code>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Deployment ID</label>
                              <code className="block text-sm bg-gray-100 px-2 py-1 rounded mt-1">{selectedProcess.deploymentId}</code>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Tenant</label>
                              <p className="text-sm text-gray-900 mt-1">{selectedProcess.tenant || 'Default'}</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Statistics</h3>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="text-2xl font-bold text-blue-600">{selectedProcess.runningInstances}</div>
                              <div className="text-xs text-blue-600">Running</div>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-lg border border-red-100">
                              <div className="text-2xl font-bold text-red-600">{selectedProcess.incidents}</div>
                              <div className="text-xs text-red-600">Incidents</div>
                            </div>
                            <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                              <div className="text-2xl font-bold text-yellow-600">{selectedProcess.failedJobs}</div>
                              <div className="text-xs text-yellow-600">Failed Jobs</div>
                            </div>
                            <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                              <div className="text-sm font-medium text-gray-700">{selectedProcess.lastDeployed}</div>
                              <div className="text-xs text-gray-500">Last Deployed</div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <button
                            onClick={(e) => openStartDialog(selectedProcess, e)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            <Play size={16} />
                            Start New Instance
                          </button>
                        </div>
                      </div>

                      {/* Right Column - Instances */}
                      <div className="col-span-2">
                        {/* Tabs */}
                        <div className="border-b border-gray-200 mb-4">
                          <div className="flex">
                            <button
                              onClick={() => { setDrawerTab('running'); setDrawerPage(0); }}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'running'
                                ? 'text-blue-600 border-blue-600'
                                : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                              Running Instances ({runningInstances.length})
                            </button>
                            <button
                              onClick={() => { setDrawerTab('history'); setDrawerPage(0); }}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'history'
                                ? 'text-blue-600 border-blue-600'
                                : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                              History ({processHistory.length})
                            </button>
                            <button
                              onClick={() => { setDrawerTab('bpmn'); setDrawerPage(0); }}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${drawerTab === 'bpmn'
                                ? 'text-blue-600 border-blue-600'
                                : 'text-gray-500 border-transparent hover:text-gray-700'
                                }`}
                            >
                              Flow Diagram
                            </button>
                          </div>
                        </div>

                        {/* Instance Table or Diagram */}
                        {drawerTab === 'bpmn' ? (
                          <div className="h-[600px] flex flex-col relative bg-gray-50 rounded-xl overflow-hidden border border-gray-200 [&_.bjs-powered-by]:hidden">
                            {/* Diagram Controls */}
                            <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
                              <div className="bg-white p-2 rounded-lg shadow border border-gray-100 flex flex-col gap-2">
                                <button onClick={() => {
                                  const canvas = bpmnViewerRef.current?.get('canvas');
                                  if (canvas) { canvas.zoom(canvas.zoom() * 1.2); setZoom(Math.round(canvas.zoom() * 100)); }
                                }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Zoom In">
                                  <ZoomIn size={18} />
                                </button>
                                <button onClick={() => {
                                  const canvas = bpmnViewerRef.current?.get('canvas');
                                  if (canvas) { canvas.zoom(canvas.zoom() / 1.2); setZoom(Math.round(canvas.zoom() * 100)); }
                                }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Zoom Out">
                                  <ZoomOut size={18} />
                                </button>
                                <button onClick={() => {
                                  const canvas = bpmnViewerRef.current?.get('canvas');
                                  if (canvas) { canvas.zoom('fit-viewport'); setZoom(Math.round(canvas.zoom() * 100)); }
                                }} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Fit to Screen">
                                  <Maximize2 size={18} />
                                </button>
                              </div>
                            </div>

                            {bpmnLoading && (
                              <div className="absolute inset-0 z-20 bg-white/80 flex items-center justify-center">
                                <Loader2 size={32} className="animate-spin text-blue-600" />
                              </div>
                            )}

                            <div ref={showFullDialog ? bpmnContainerRef : undefined} className="w-full h-full" />

                            <div className="absolute bottom-4 left-4 z-10 bg-white px-3 py-1.5 rounded-lg shadow border border-gray-100 text-xs font-medium text-gray-500">
                              Zoom: {zoom}%
                            </div>
                          </div>
                        ) : historyLoading ? (
                          <div className="flex items-center justify-center p-8">
                            <Loader2 size={24} className="animate-spin text-blue-600" />
                          </div>
                        ) : (
                          <>
                            {(() => {
                              const items = drawerTab === 'running' ? runningInstances : processHistory;
                              const dialogPageSize = 10;
                              const totalDialogPages = Math.ceil(items.length / dialogPageSize);
                              const paginatedItems = items.slice(drawerPage * dialogPageSize, (drawerPage + 1) * dialogPageSize);

                              if (items.length === 0) {
                                return (
                                  <div className="text-center py-8 text-gray-500">
                                    No {drawerTab === 'running' ? 'running instances' : 'history'} available
                                  </div>
                                );
                              }

                              return (
                                <>
                                  <div className="grid grid-cols-2 gap-4">
                                    {paginatedItems.map((item) => (
                                      <div
                                        key={item.id}
                                        className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg cursor-pointer transition-all duration-200 group"
                                        onClick={() => navigate(`/processes/instance/${item.instanceId}`)}
                                      >
                                        {/* Header Row */}
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <span className="font-semibold text-gray-900 truncate">
                                              {item.businessKey || selectedProcess?.name || 'Process Instance'}
                                            </span>
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 ${item.status === 'running' ? 'bg-blue-100 text-blue-700' :
                                              item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                item.status === 'canceled' ? 'bg-gray-100 text-gray-700' :
                                                  'bg-red-100 text-red-700'
                                              }`}>
                                              {item.status === 'running' && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
                                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                                            </span>
                                            {item.hasIncident && (
                                              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                                                <AlertCircle size={12} />
                                                Incident
                                              </span>
                                            )}
                                          </div>
                                          <ArrowRight size={18} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                                        </div>

                                        {/* Instance Info Row */}
                                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-sm text-gray-500 mb-4">
                                          <code className="bg-gray-100 px-2 py-0.5 rounded font-mono text-xs">{item.instanceId.substring(0, 16)}...</code>
                                          <span>•</span>
                                          <span>Started: {item.time}</span>
                                          <span>•</span>
                                          <span>Duration: {item.duration}</span>
                                        </div>

                                        {/* Progress Section (only for running) */}
                                        {item.status === 'running' && item.currentActivity && (
                                          <div className="mb-4">
                                            <div className="flex items-center justify-between mb-1.5">
                                              <span className="text-sm text-blue-600 font-medium">Current Activity: {item.currentActivity}</span>
                                              <span className="text-sm font-semibold text-gray-700">{item.progress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                              <div
                                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${item.progress}%` }}
                                              ></div>
                                            </div>
                                          </div>
                                        )}

                                        {/* Footer Stats */}
                                        <div className="flex items-center gap-5 text-sm text-gray-500 pt-3 border-t border-gray-100">
                                          <div className="flex items-center gap-1.5">
                                            <Database size={14} className="text-gray-400" />
                                            <span>{item.variables} variables</span>
                                          </div>
                                          {item.status === 'running' && item.activeTasks && item.activeTasks > 0 && (
                                            <div className="flex items-center gap-1.5">
                                              <GitBranch size={14} className="text-gray-400" />
                                              <span>{item.activeTasks} active tasks</span>
                                            </div>
                                          )}
                                          <div className="flex items-center gap-1.5">
                                            <User size={14} className="text-gray-400" />
                                            <span>{item.user}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {/* Dialog Pagination */}
                                  {totalDialogPages > 1 && (
                                    <div className="flex items-center justify-between mt-4">
                                      <span className="text-sm text-gray-500">
                                        Showing {drawerPage * dialogPageSize + 1} to {Math.min((drawerPage + 1) * dialogPageSize, items.length)} of {items.length}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => setDrawerPage(Math.max(0, drawerPage - 1))}
                                          disabled={drawerPage === 0}
                                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          Previous
                                        </button>
                                        <button
                                          onClick={() => setDrawerPage(Math.min(totalDialogPages - 1, drawerPage + 1))}
                                          disabled={drawerPage >= totalDialogPages - 1}
                                          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          Next
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Start Process Dialog — same component the Studio page uses,
                so the two screens stay visually identical. The dialog
                handles its own form fetch, businessKey input, and the
                actual startById call. We just refresh the process list
                after a successful start. */}
            <StartProcessDialog
              isOpen={showStartDialog && !!processToStart}
              onClose={() => {
                setShowStartDialog(false);
                setProcessToStart(null);
              }}
              definitionId={processToStart?.definitionId ?? ''}
              name={processToStart?.name ?? ''}
              version={processToStart?.version ?? ''}
              onStarted={() => {
                fetchProcesses();
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessesPage;
